import { usePageStore } from '@/store/usePageStore';
import { TScrollAnimation } from '@/types/global';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { usePathname } from 'next/navigation';

gsap.registerPlugin(ScrollTrigger);


export const useScrollAnimation = ({
  elContainer,
  animationType,
}: {
  elContainer: (HTMLElement | HTMLElement[] | null)[];
  animationType: TScrollAnimation;
}) => {
  const pathname = usePathname();
  const isSmootherReady = usePageStore((s) => s.isSmootherReady);

  const toEls = () => {
    if (!elContainer) return [] as HTMLElement[];
    if ('current' in (elContainer as any)) {
      return ((elContainer as any).current || []).filter(Boolean) as HTMLElement[];
    }
    return (Array.isArray(elContainer) ? elContainer : [elContainer]).filter(Boolean) as HTMLElement[];
  };

  useGSAP(() => {
    if (!animationType || animationType === 'none' || !isSmootherReady) return;
    if (typeof window === 'undefined') return;

    let ctx: gsap.Context | undefined;
    let raf = 0;

    const init = () => {
      const els = toEls();
      if (!els.length) {
        // wait until refs are set (refs don’t cause a re-render)
        raf = requestAnimationFrame(init);
        return;
      }

      ctx = gsap.context(() => {
        if (animationType === 'scaleInOnScroll') {
          els.forEach((el) => {
            gsap.set(el, { scale: 1 });
            gsap.to(el, {
              scale: 1.2,
              ease: 'power1.out',
              scrollTrigger: {
                trigger: el,          // one trigger per element
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
              },
            });
          });
        }

        if (animationType === 'fadeOutOnScroll') {
          els.forEach((el) => {
            gsap.set(el, { opacity: 1, y: 0 });
            gsap.to(el, {
              opacity: 0,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top 15%',
                end: 'top top',
                scrub: true,
              },
            });
          });
        }
      });
    };

    init();
    return () => {
      if (ctx) ctx.revert();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname, isSmootherReady, animationType]);
};